import { Answer } from 'types/answers'
import { Quest } from 'types/quests'

export type PendingAnswerOutboxItem = {
    answerId: string
    questId: string
    answer: Partial<Answer> & Record<string, unknown>
    childQuestIds: string[]
    createdAtMs: number
    updatedAtMs: number
    confirmedAtMs?: number
    retryCount?: number
}

const STORAGE_KEY_PREFIX = 'knov:pendingAnswerOutbox:v1'
const PENDING_ANSWER_OUTBOX_TTL_MS = 24 * 60 * 60 * 1000

function getStorageKey() {
    const userId = (globalThis as { gon?: { currentUser?: { id?: string | number } } })?.gon
        ?.currentUser?.id
    return `${STORAGE_KEY_PREFIX}:${userId ? String(userId) : 'anonymous'}`
}

function getStorage(): Storage | null {
    try {
        if (typeof localStorage !== 'undefined') return localStorage
        if (typeof window !== 'undefined' && window.localStorage) return window.localStorage
    } catch (error) {}
    return null
}

function readOutbox(): PendingAnswerOutboxItem[] {
    const storage = getStorage()
    if (!storage) return []

    try {
        const raw = storage.getItem(getStorageKey())
        if (!raw) return []
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []

        const cutoff = Date.now() - PENDING_ANSWER_OUTBOX_TTL_MS
        return parsed
            .filter(item => item?.answerId && item?.questId && item?.answer)
            .filter(item => typeof item.createdAtMs !== 'number' || item.createdAtMs >= cutoff)
            .map(item => ({
                ...item,
                answerId: String(item.answerId),
                questId: String(item.questId),
                childQuestIds: Array.isArray(item.childQuestIds)
                    ? item.childQuestIds.filter(Boolean).map(String)
                    : [],
            }))
    } catch (error) {
        return []
    }
}

function writeOutbox(items: PendingAnswerOutboxItem[]) {
    const storage = getStorage()
    if (!storage) return

    try {
        const storageKey = getStorageKey()
        if (items.length) storage.setItem(storageKey, JSON.stringify(items))
        else storage.removeItem(storageKey)
    } catch (error) {}
}

function isFileLike(value: unknown): boolean {
    if (!value || typeof value !== 'object') return false
    if (typeof File !== 'undefined' && value instanceof File) return true
    if (typeof Blob !== 'undefined' && value instanceof Blob) return true
    return false
}

function containsFileLike(value: unknown): boolean {
    if (!value || typeof value !== 'object') return false
    if (isFileLike(value)) return true
    if (Array.isArray(value)) return value.some(containsFileLike)
    return Object.values(value as Record<string, unknown>).some(containsFileLike)
}

function sanitizeAnswerForStorage(answer: Partial<Answer> & Record<string, unknown>) {
    if (containsFileLike(answer)) return null

    try {
        return JSON.parse(
            JSON.stringify({
                ...answer,
                isLocal: true,
                isPendingOutbox: true,
            }),
        )
    } catch (error) {
        return null
    }
}

function questServerAnswerIds(quest: Partial<Quest> | null | undefined) {
    return new Set(
        [
            ...(Array.isArray(quest?.sorted_answer_ids) ? quest.sorted_answer_ids : []),
            ...(Array.isArray(quest?.sorted_answers)
                ? quest.sorted_answers.map(answer => answer?.id)
                : []),
        ]
            .filter(Boolean)
            .map(String),
    )
}

export function upsertPendingAnswerOutbox(answer: Partial<Answer> & Record<string, unknown>) {
    if (!answer?.id || !answer?.quest_id) return false

    const storedAnswer = sanitizeAnswerForStorage(answer)
    if (!storedAnswer) return false

    const answerId = String(answer.id)
    const questId = String(answer.quest_id)
    const now = Date.now()
    const childQuestIds = Array.isArray(answer.child_quests)
        ? answer.child_quests
              .map(childQuest => childQuest?.id)
              .filter(Boolean)
              .map(String)
        : []
    const items = readOutbox()
    const existingIndex = items.findIndex(item => item.answerId === answerId)
    const existing = existingIndex >= 0 ? items[existingIndex] : null
    const nextItem: PendingAnswerOutboxItem = {
        ...existing,
        answerId,
        questId,
        answer: storedAnswer,
        childQuestIds,
        createdAtMs: existing?.createdAtMs || now,
        updatedAtMs: now,
        retryCount: existing?.retryCount || 0,
    }

    const nextItems =
        existingIndex >= 0
            ? items.map((item, index) => (index === existingIndex ? nextItem : item))
            : [...items, nextItem]
    writeOutbox(nextItems)
    return true
}

export function getPendingAnswerOutboxItems() {
    const items = readOutbox()
    writeOutbox(items)
    return items
}

export function getPendingAnswerOutboxItemsForQuest(questId: string | number | null | undefined) {
    if (questId == null) return []
    const id = String(questId)
    return getPendingAnswerOutboxItems().filter(item => item.questId === id)
}

export function getPendingAnswerOutboxAnswersForQuest(questId: string | number | null | undefined) {
    return getPendingAnswerOutboxItemsForQuest(questId).map(item => item.answer)
}

export function hasPendingAnswerOutboxItem(answerId: string | number | null | undefined) {
    if (answerId == null) return false
    const id = String(answerId)
    return getPendingAnswerOutboxItems().some(item => item.answerId === id)
}

export function markPendingAnswerOutboxConfirmed(answerId: string | number | null | undefined) {
    if (answerId == null) return
    const id = String(answerId)
    const now = Date.now()
    writeOutbox(
        getPendingAnswerOutboxItems().map(item =>
            item.answerId === id ? { ...item, confirmedAtMs: item.confirmedAtMs || now } : item,
        ),
    )
}

export function removePendingAnswerOutbox(answerId: string | number | null | undefined) {
    if (answerId == null) return
    const id = String(answerId)
    writeOutbox(getPendingAnswerOutboxItems().filter(item => item.answerId !== id))
}

export function shouldRemovePendingAnswerOutboxForStatus(status: number | null | undefined) {
    if (typeof status !== 'number') return false
    if (status === 404 || status === 429) return false
    return status >= 400 && status < 500
}

export function removePendingAnswerOutboxConfirmedByQuest(
    quest: Partial<Quest> | null | undefined,
) {
    const serverIds = questServerAnswerIds(quest)
    if (!serverIds.size) return

    writeOutbox(
        getPendingAnswerOutboxItems().filter(item => {
            if (String(item.questId) !== String(quest?.id)) return true
            return !serverIds.has(item.answerId)
        }),
    )
}
